import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage, API_URL } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2, Plus, Search, Edit, Trash2, Users, ChevronRight, Layers
} from 'lucide-react';

export const FacultiesPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFaculty, setSelectedFaculty] = useState(null);

  useEffect(() => {
    fetchFaculties();
  }, []);

  const fetchFaculties = async () => {
    try {
      // Get university ID from user or use first available
      const uniResponse = await axios.get(`${API_URL}/universities`);
      if (uniResponse.data.length > 0) {
        const uniId = uniResponse.data[0].id;
        const response = await axios.get(`${API_URL}/universities/${uniId}/faculties`);
        setFaculties(response.data);
      }
    } catch (error) {
      console.error('Error fetching faculties:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteFaculty = async (facultyId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer cette faculté ?' : 'Delete this faculty?')) return;
    
    try {
      await axios.delete(`${API_URL}/faculties/${facultyId}`);
      toast.success(language === 'fr' ? 'Faculté supprimée' : 'Faculty deleted');
      fetchFaculties();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const filteredFaculties = faculties.filter(f =>
    f.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-slide-in" data-testid="faculties-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2">{t('faculties')}</h1>
          <p className="text-gray-500 mt-1">
            {language === 'fr' 
              ? `${faculties.length} facultés`
              : `${faculties.length} faculties`}
          </p>
        </div>
        <AddFacultyDialog onAdd={fetchFaculties} />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Faculties Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-6 w-48 bg-gray-200 rounded mb-4" />
                <div className="h-4 w-32 bg-gray-200 rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredFaculties.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {language === 'fr' ? 'Aucune faculté' : 'No faculties'}
            </h3>
            <p className="text-gray-500">
              {language === 'fr' ? 'Créez votre première faculté' : 'Create your first faculty'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFaculties.map(faculty => (
            <FacultyCard
              key={faculty.id}
              faculty={faculty}
              onEdit={() => setSelectedFaculty(faculty)}
              onDelete={() => deleteFaculty(faculty.id)}
              language={language}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      {selectedFaculty && (
        <EditFacultyDialog
          faculty={selectedFaculty}
          onClose={() => setSelectedFaculty(null)}
          onSave={fetchFaculties}
        />
      )}
    </div>
  );
};

const FacultyCard = ({ faculty, onEdit, onDelete, language, t }) => (
  <Card className="card-hover group" data-testid={`faculty-card-${faculty.code}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
          <Building2 className="w-6 h-6 text-blue-600" />
        </div>
        <Badge variant="secondary" className="font-mono">{faculty.code}</Badge>
      </div>
      
      <h3 className="font-semibold text-gray-900 mb-2">{faculty.name}</h3>
      {faculty.description && (
        <p className="text-sm text-gray-500 mb-4 line-clamp-2">{faculty.description}</p>
      )}
      
      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="ghost" size="sm" className="gap-1 text-gray-500">
          <Layers className="w-4 h-4" />
          {language === 'fr' ? 'Départements' : 'Departments'}
          <ChevronRight className="w-4 h-4" />
        </Button>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={onEdit}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onDelete} className="text-red-500 hover:text-red-700">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

const AddFacultyDialog = ({ onAdd }) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', code: '', description: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Get university ID
      const uniResponse = await axios.get(`${API_URL}/universities`);
      if (uniResponse.data.length === 0) {
        toast.error(language === 'fr' ? 'Aucune université trouvée' : 'No university found');
        return;
      }
      const uniId = uniResponse.data[0].id;
      
      await axios.post(`${API_URL}/universities/${uniId}/faculties`, formData);
      toast.success(language === 'fr' ? 'Faculté créée' : 'Faculty created');
      setOpen(false);
      setFormData({ name: '', code: '', description: '' });
      onAdd?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="add-faculty-button">
          <Plus className="w-4 h-4" />
          {language === 'fr' ? 'Ajouter une faculté' : 'Add Faculty'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{language === 'fr' ? 'Nouvelle faculté' : 'New Faculty'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t('name')}</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Faculté des Sciences"
              required
            />
          </div>
          <div>
            <Label>{t('code')}</Label>
            <Input
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              placeholder="FSC"
              required
            />
          </div>
          <div>
            <Label>{t('description')}</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={language === 'fr' ? 'Description de la faculté...' : 'Faculty description...'}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('loading') : t('save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditFacultyDialog = ({ faculty, onClose, onSave }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: faculty.name,
    code: faculty.code,
    description: faculty.description || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API_URL}/faculties/${faculty.id}`, formData);
      toast.success(language === 'fr' ? 'Faculté mise à jour' : 'Faculty updated');
      onClose();
      onSave?.();
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{language === 'fr' ? 'Modifier la faculté' : 'Edit Faculty'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t('name')}</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>
          <div>
            <Label>{t('code')}</Label>
            <Input
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              required
            />
          </div>
          <div>
            <Label>{t('description')}</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('loading') : t('save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FacultiesPage;
